import React, { useState } from 'react';
import { connect } from 'react-redux';
import Moment from "react-moment";
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import moment from 'moment';

import './style.scss';

const GET_COMMENTS_HISTORY = gql`
  query CommentsHistory($token: String!, $page: Int) {
    commentsHistory(
      input: {
        token: $token,
        page: $page,
        first: 20,
      }) {
        data {
          uuid
          comment
          page_url
          created_at
          deleted_at
          edited
          snapshots {
            url
          }
          images {
            url
          }
          videos {
            url
          }
        }
        paginatorInfo {
          total
        }
    }
  }
`;

const GET_MEMBER_COMMENTS_HISTORY = gql`
  query CommentsHistory($token: String!, $username: String!, $page: Int) {
    publicCommentsHistory(
      input: {
        username: $username,
        token: $token,
        page: $page,
        first: 20,
      }) {
        data {
          uuid
          comment
          page_url
          created_at
          deleted_at
          edited
          snapshots {
            url
          }
          images {
            url
          }
          videos {
            url
          }
        }
        paginatorInfo {
          total
        }
    }
  }
`;

const plainLinkReplacer = (match, p1, p2, p3) => {
  if (p1) {
    return match
  }
  // we should remove all html tags from the link
  const url = new URL(p3.replace(/<[^>]+>/g, ''))
  return `<a href="${url}" rel="noopener noreferrer" target="_blank">${url}</a>`
}

const transformPlainLinksInHTMLIntoATags = (html) => {
  // skip links that are already wrapped in <a></a> tag, transform only plain links

  return html.replace(
    // Safari completely breaks with lookbehinds, so this utilizes this regex from https://stackoverflow.com/a/11864377
    /((href|src)=["']|)(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi,
    plainLinkReplacer,
  )
}

const replaceImageTagsWithImageUrls = (html) => {
  return html.replace(/<img[^>]+src="([^">]+)"[^>]*>/gi, (match, p1) => {
    return p1
  })
}

function CommentsSection(props) {
  const { token, username } = props;
  const [page, setPage] = useState(1);
  const [comments, setComments] = useState([]);
  const [totalComments, setTotalComments] = useState(0);

  const { loading, error } = useQuery(
    username ? GET_MEMBER_COMMENTS_HISTORY : GET_COMMENTS_HISTORY, {
      variables: { token, page, username },
      fetchPolicy: 'cache-first',
      onCompleted: (d) => {
        if (username && d.publicCommentsHistory.data) {
          setComments(comments.concat(d.publicCommentsHistory.data));
          setTotalComments(d.publicCommentsHistory.paginatorInfo.total);
        }
        if (!username && d.commentsHistory.data) {
          setComments(comments.concat(d.commentsHistory.data));
          setTotalComments(d.commentsHistory.paginatorInfo.total);
        }
      },
    }
  );

  return (
    <div className="row bg-fff-80 my-3 pb-3 comments-section">
      <div className="col-12">
        <div>
          <h2 className="py-3 d-inline-block">
            Comment History
          </h2>
        </div>

        <div>
          {!loading && comments.length === 0 && (
            <div>
              <p>
                {error ? error.message : 'No comments found.'}
              </p>
            </div>
          )}

          {comments?.map(comment => (
            <div key={comment.uuid} className="comment">
              <div
                className="comment__body"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: transformPlainLinksInHTMLIntoATags(
                    replaceImageTagsWithImageUrls(comment.comment),
                  )
                }}
                aria-label={comment.comment.replace(/<[^>]*>/g, '')}
              />

              {Boolean(comment.images?.length || comment.snapshots?.length) && (
                <div className="comment__images">
                  {[...comment.images || [], ...comment.snapshots || []]
                    .map((image, key) => (
                      <a
                        key={`comment-image-${key}`}
                        href={image.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {image.url}
                      </a>
                  ))}
                </div>
              )}

              <div className="comment__videos">
                {comment.videos?.map((video, key) => {
                  const url = video.url.split('" ')[0];
                  return (
                    <a
                      key={`comment-video-${key}`}
                      href={url}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {url}
                    </a>
                )})}
              </div>

              {comment.edited && (
                <div className="comment__edited">(edited)</div>
              )}

              <div className="comment__meta">
                <span className="comment__date">
                  <Moment format="MMMM Do YYYY, h:mm a" tz={moment.tz.guess()}>
                    {moment.utc(comment.created_at)}
                  </Moment>
                </span>

                {comment.page_url && (
                <a
                  href={`${comment.page_url}?commentId=${comment.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See original comment
                </a>
                )}
                {comment.deleted_at && (
                <span className="comment__deleted">
                  Comment has been deleted
                </span>
                )}

              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div>
            <p>Loading...</p>
          </div>
        )}

        {!loading && totalComments > (page * 20) && (
          <div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setPage(page + 1)}
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    token: state.auth.commentToken,
  };
};

CommentsSection.propTypes = {
  token: PropTypes.string.isRequired,
  username: PropTypes.string,
};

export default connect(mapStateToProps)(CommentsSection);
